import type {
  PathologistReviewQueueCase,
  IncomingLabCase,
} from "../../services";

export interface QueueColumnDef {
  id: string;
  label: string;
  defaultVisible: boolean;
  sortable: boolean;
  width?: number;
  tooltip?: string;
}

export const ASSIGNED_PATHOLOGIST_COLUMN: QueueColumnDef = {
  id: "assignedPathologist",
  label: "Assigned Pathologist",
  defaultVisible: true,
  sortable: true,
  width: 180,
  tooltip: "The pathologist currently assigned to review the case.",
};

export const TC_REVIEW_ACTION_COLUMN: QueueColumnDef = {
  id: "tcReviewAction",
  label: "Action",
  defaultVisible: true,
  sortable: false,
  width: 190,
  tooltip: "Complete the assigned acknowledgement for a TC technical review.",
};

export const SEND_OUT_STATUS_COLUMN: QueueColumnDef = {
  id: "sendOutStatus",
  label: "Send-Out Status",
  defaultVisible: true,
  sortable: false,
  width: 200,
  tooltip: "Shipping and returned report status for the send-out.",
};

export const ALL_QUEUE_COLUMNS: QueueColumnDef[] = [
  {
    id: "flag",
    label: "Flag",
    defaultVisible: true,
    sortable: true,
    width: 80,
    tooltip:
      "Priority and alert indicators. STAT = rush case requiring immediate attention. HIGH = high-priority case. A red flag appears when the case has exceeded the overdue TAT threshold. A purple icon appears when the case has an open discussion.",
  },
  {
    id: "caseNumber",
    label: "Case #",
    defaultVisible: true,
    sortable: true,
    width: 150,
    tooltip:
      "The unique accession number assigned to the case when it was received by the lab (e.g. 26-G-00025). The external case number from the referring facility is shown below it when available.",
  },
  {
    id: "patient",
    label: "Patient",
    defaultVisible: true,
    sortable: true,
    width: 180,
    tooltip: "Patient full name displayed as Last name, First name.",
  },
  {
    id: "dob",
    label: "DOB",
    defaultVisible: true,
    sortable: true,
    width: 110,
    tooltip:
      "Patient date of birth. Used to confirm correct patient identity before reviewing. Age is shown in parentheses.",
  },
  {
    id: "physician",
    label: "Physician",
    defaultVisible: true,
    sortable: true,
    width: 160,
    tooltip:
      "The referring physician who sent the specimen to GCP and requested the pathology opinion.",
  },
  {
    id: "caseType",
    label: "Review Type",
    defaultVisible: true,
    sortable: true,
    width: 130,
    tooltip:
      "The type of pathologist action required. Initial Review = first-time read. Malignancy = a cancer notification exists for this case. Addendum = additional report after a prior sign-off. Amendment or Correction = a previously signed report is being revised.",
  },
  {
    id: "tat",
    label: "TAT",
    defaultVisible: true,
    sortable: true,
    width: 80,
    tooltip:
      "Turnaround Time — how long the lab has had this case since it was received. Shows 'Today', '1 day', '2 days', etc. A red flag appears in the Flag column when the case exceeds the configured overdue threshold.",
  },
  {
    id: "pendingTests",
    label: "Pending Tests",
    defaultVisible: true,
    sortable: true,
    width: 130,
    tooltip:
      "Indicates whether the case has outstanding additional stain orders that have not yet been completed. Shows the number of stains still pending. The case remains in the queue so the pathologist can track it.",
  },
  {
    id: "step",
    label: "Step",
    defaultVisible: true,
    sortable: true,
    width: 110,
    tooltip:
      "The current status of the case in the workflow. 'Pending Review' = queued for the pathologist. 'In Review' = pathologist has started. 'Awaiting Stains' = review is complete but additional stain results are still pending. 'Signed Off' = fully complete.",
  },
  {
    id: "qaReview",
    label: "QA",
    defaultVisible: true,
    sortable: false,
    width: 60,
    tooltip:
      "Indicates whether the case has a pending QA (Quality Assurance) review — typically an external opinion or send-out comparison that requires pathologist attention. Shows Yes or No.",
  },
  {
    id: "specimens",
    label: "Specimens",
    defaultVisible: false,
    sortable: true,
    width: 120,
    tooltip:
      "Total number of specimen parts submitted with this case, along with the total slide count including any additional stain slides.",
  },
  {
    id: "facility",
    label: "Facility",
    defaultVisible: false,
    sortable: true,
    width: 150,
    tooltip: "The GCP facility location where this case is being processed.",
  },
  {
    id: "workflow",
    label: "Workflow",
    defaultVisible: false,
    sortable: true,
    width: 130,
    tooltip:
      "The accessioning workflow type for this case: Global (full internal processing), Pass Through (external slides, GCP diagnoses), Consultation (second opinion request), Full TC or Partial TC (technical component only).",
  },
  {
    id: "procedure",
    label: "Procedure",
    defaultVisible: false,
    sortable: true,
    width: 160,
    tooltip:
      "The clinical procedure or test type requested on the requisition (e.g. biopsy, excision, pap smear).",
  },
  {
    id: "practice",
    label: "Practice",
    defaultVisible: false,
    sortable: true,
    width: 160,
    tooltip:
      "The sending medical practice or clinic that submitted the case to GCP.",
  },
  {
    id: "mrn",
    label: "MRN",
    defaultVisible: false,
    sortable: true,
    width: 120,
    tooltip:
      "Medical Record Number — the patient's unique identifier used by the ordering practice or health system.",
  },
  {
    id: "dateCollected",
    label: "Date Collected",
    defaultVisible: false,
    sortable: true,
    width: 120,
    tooltip:
      "The date the specimen was collected from the patient, as recorded on the requisition.",
  },
  {
    id: "priority",
    label: "Priority",
    defaultVisible: false,
    sortable: true,
    width: 100,
    tooltip:
      "Explicit priority level for the case. STAT = rush, must be reviewed immediately. HIGH = elevated priority. Normal = standard review order.",
  },
  {
    id: "signedOffAt",
    label: "Signed Off",
    defaultVisible: false,
    sortable: true,
    width: 150,
    tooltip:
      "The date and time the case was signed off by the pathologist. Completed cases are automatically ordered by this date (newest first).",
  },
];

export interface QueuePreferences {
  columns: string[];
  overdueThresholdDays: number;
}

export const DEFAULT_QUEUE_PREFERENCES: QueuePreferences = {
  columns: ALL_QUEUE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id),
  overdueThresholdDays: 2,
};

export const PREFERENCE_KEY = "pathologist_queue_columns";

// Re-export PathologistReviewQueueCase for convenience
export type { PathologistReviewQueueCase };

// ==================== INCOMING TAB COLUMN CONFIG ====================

export const ALL_INCOMING_COLUMNS: QueueColumnDef[] = [
  {
    id: "flag",
    label: "Flag",
    tooltip:
      "Priority and alert indicators. STAT = rush case requiring immediate attention. HIGH = high-priority case.",
    defaultVisible: true,
    sortable: true,
    width: 80,
  },
  {
    id: "caseNumber",
    label: "Case #",
    tooltip:
      "The unique accession number assigned to the case when it was received by the lab. The external case number from the referring facility is shown below it when available.",
    defaultVisible: true,
    sortable: true,
    width: 150,
  },
  {
    id: "patient",
    label: "Patient",
    tooltip: "Patient full name displayed as Last name, First name.",
    defaultVisible: true,
    sortable: true,
    width: 180,
  },
  {
    id: "dob",
    label: "DOB",
    tooltip:
      "Patient date of birth. Used to confirm correct patient identity. Age is shown in parentheses.",
    defaultVisible: true,
    sortable: true,
    width: 110,
  },
  {
    id: "physician",
    label: "Physician",
    tooltip:
      "The referring physician who sent the specimen to GCP and requested the pathology opinion.",
    defaultVisible: true,
    sortable: true,
    width: 160,
  },
  {
    id: "currentStep",
    label: "Current Step",
    tooltip:
      "The current processing stage the case is in (e.g., Grossing, Cutting, Staining, Awaiting Send-Out Report, Awaiting Stains).",
    defaultVisible: true,
    sortable: true,
    width: 160,
  },
  {
    id: "tat",
    label: "TAT",
    tooltip:
      "Turnaround Time — how long the lab has had this case since it was received. Shows 'Today', '1 day', '2 days', etc.",
    defaultVisible: true,
    sortable: true,
    width: 80,
  },
  {
    id: "dateReceived",
    label: "Date Received",
    tooltip: "The date the case was received by the lab.",
    defaultVisible: false,
    sortable: true,
    width: 120,
  },
  {
    id: "dateCollected",
    label: "Date Collected",
    tooltip:
      "The date the specimen was collected from the patient, as recorded on the requisition.",
    defaultVisible: false,
    sortable: true,
    width: 120,
  },
  {
    id: "specimens",
    label: "Specimens",
    tooltip: "Total number of specimen parts submitted with this case.",
    defaultVisible: false,
    sortable: true,
    width: 110,
  },
  {
    id: "procedure",
    label: "Procedure",
    tooltip:
      "The clinical procedure or test type requested on the requisition (e.g. biopsy, excision, pap smear).",
    defaultVisible: false,
    sortable: true,
    width: 160,
  },
  {
    id: "practice",
    label: "Practice",
    tooltip:
      "The sending medical practice or clinic that submitted the case to GCP.",
    defaultVisible: false,
    sortable: true,
    width: 160,
  },
  {
    id: "workflow",
    label: "Workflow",
    tooltip:
      "The accessioning workflow type for this case: Global (full internal processing), Pass Through, Consultation, etc.",
    defaultVisible: false,
    sortable: true,
    width: 130,
  },
  {
    id: "mrn",
    label: "MRN",
    tooltip:
      "Medical Record Number — the patient's unique identifier used by the ordering practice or health system.",
    defaultVisible: false,
    sortable: true,
    width: 120,
  },
  {
    id: "priority",
    label: "Priority",
    tooltip:
      "Explicit priority level for the case. STAT = rush, must be processed immediately. HIGH = elevated priority. Normal = standard order.",
    defaultVisible: false,
    sortable: true,
    width: 100,
  },
  {
    id: "facility",
    label: "Facility",
    tooltip: "The GCP facility location where this case is being processed.",
    defaultVisible: false,
    sortable: true,
    width: 150,
  },
];

export const DEFAULT_INCOMING_COLUMN_ORDER = ALL_INCOMING_COLUMNS.filter(
  (c) => c.defaultVisible
).map((c) => c.id);

export const INCOMING_PREFERENCE_KEY = "pathologist_incoming_columns";

export interface IncomingQueuePreferences {
  columns: string[];
}

export const DEFAULT_INCOMING_PREFERENCES: IncomingQueuePreferences = {
  columns: DEFAULT_INCOMING_COLUMN_ORDER,
};

// Re-export IncomingLabCase for convenience
export type { IncomingLabCase };
